import React from 'react';
import { useTranslation } from 'react-i18next';
import { useEvent } from 'react-use';
import { Icon } from 'semantic-ui-react';

import { bem } from '../../core/design/bem';
import { useRefreshUserOnRegistration } from '../../hooks/useRefreshUserOnRegistrayion';
import './FullscreenFormModal.scss';
import { buildRegistrationFormUrl, useRegistrationsFormBaseUrl } from './registrationForm';

const css = bem('FullscreenFormModal');

type FullscreenFormModalProps = {
  /** Slug du formulaire registrations */
  formSlug: string;
  /** Titre affiché dans le header */
  title: string;
  /** Ouvre le formulaire en édition sur cet enregistrement */
  privateKey?: string;
  /** Paramètres de query supplémentaires (ex. `{ parentId }`) */
  query?: Record<string, string | undefined>;
  onClose: () => void;
  /**
   * Appelé quand le formulaire signale sa soumission
   * (`registration:step` / `confirmation`). Par défaut : `onClose`.
   */
  onCompleted?: () => void;
};

/**
 * Overlay plein écran affichant un formulaire registrations dans une iframe.
 * Se ferme sur Échap et à la soumission du formulaire.
 */
const FullscreenFormModal = ({
  formSlug,
  title,
  privateKey,
  query,
  onClose,
  onCompleted,
}: FullscreenFormModalProps): JSX.Element => {
  const { t } = useTranslation();
  const baseUrl = useRegistrationsFormBaseUrl();
  const url = buildRegistrationFormUrl(baseUrl, formSlug, { privateKey, query });

  // Le formulaire registrations émet `registration:step` / `confirmation`.
  useRefreshUserOnRegistration(onCompleted || onClose);

  useEvent('keydown', (evt: KeyboardEvent) => {
    if (evt.key === 'Escape') onClose();
  });

  return (
    <div className={css().toString()} role="dialog" aria-modal="true" aria-label={title}>
      <div className={css('header').toString()}>
        <span className={css('title').toString()}>{title}</span>
        <Icon
          name="close"
          link
          className={css('close').toString()}
          onClick={onClose}
          aria-label={t('btn.close')}
        />
      </div>
      <iframe className={css('iframe').toString()} title={title} src={url} allowTransparency />
    </div>
  );
};

FullscreenFormModal.defaultProps = {
  privateKey: undefined,
  query: undefined,
  onCompleted: undefined,
};

export default FullscreenFormModal;
